// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
    Box,
    Button,
    ButtonDropdown,
    Modal,
    SpaceBetween,
    StatusIndicator,
    Select,
} from '@cloudscape-design/components';
import {useDiagramSettingsState} from '../../../Contexts/DiagramSettingsContext';
import {useResourceState} from '../../../Contexts/ResourceContext';
import {
    clearGraph,
    fetchResources,
    fitToViewport,
    focusOnResources,
    groupResources,
    removeResource,
    hideCosts,
    addResources,
} from '../Canvas/Commands/CanvasCommands';
import {Prompt, useHistory, useParams} from 'react-router-dom';
import {DRAW} from '../../../../routes';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import {
    diagramsPrefix,
    usePutObject,
    useRemoveObject,
} from '../../../Hooks/useS3Objects';
import {usePrevious} from 'react-use';
import {DEFAULT_COSTS_INTERVAL} from '../../../../config/constants';
import ExportDiagramModal from '../Canvas/Export/ExportDiagramModal';
import {getVisibleElements, getVisibleNodesFromSelection} from '../../../../Utils/DiagramFilteringUtils';
import {useFilterPersistence} from '../../../Contexts/FilterPersistenceContext';
import {useExperimentsState} from '../../../Contexts/ExperimentsContext';
import {getResourceTypesForViewPreset, VIEW_PRESET_TYPES} from '../../../../Utils/ResourceFilteringUtils';
import {getStandardLayout} from '../Canvas/Layout/StandardGraphLayout';
import {useGetAccountResourceGraph} from '../../../Hooks/useGetResourceGraph';
import {useResourcesMetadata} from '../../../Hooks/useResourcesMetadata';

const PRESET_STORAGE_PREFIX = 'diagram_viewPreset';
// Cached view presets older than this are discarded on read
const PRESET_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const getViewPresetStorageKey = (diagramName, visibility) =>
    `${PRESET_STORAGE_PREFIX}_${visibility}_${diagramName}`;

function normalizeInterval(interval) {
    if (interval.type !== 'relative') return interval;

    return {
        type: 'relative',
        startDate: dayjs()
            .subtract(interval.amount, interval.unit)
            .format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
    };
}

// Returns the display label for a given view preset value
const getViewPresetLabel = (viewPresetValue) => {
    switch (viewPresetValue) {
        case VIEW_PRESET_TYPES.FULL:
            return 'All AWS Resources';
        case VIEW_PRESET_TYPES.BUILDER:
            return 'Common AWS Builder Resources';
        case VIEW_PRESET_TYPES.SECURITY:
            return 'Common AWS Security Resources';
        default:
            return 'All AWS Resources';
    }
};

const DiagramControlPanel = ({settings}) => {
    const [{canvas, selectedResources, resources}, dispatchCanvas] =
        useDiagramSettingsState();
    const [{graphResources, diagramSettings}] = useResourceState();
    const {name, visibility} = useParams();
    const history = useHistory();
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showExport, setShowExport] = React.useState(false);
    const [statusMessage, setStatusMessage] = useState();
    const [hasLoadedCosts, setHasLoadedCosts] = useState(false);
    const {endDate, startDate} = normalizeInterval(
        settings?.costInterval ?? DEFAULT_COSTS_INTERVAL
    );
    const {removeAsync} = useRemoveObject(diagramsPrefix);
    const {putAsync} = usePutObject(diagramsPrefix);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const previousResources = usePrevious(resources);

    // Get filter persistence context for Save functionality
    const {viewMode, updateFilters} = useFilterPersistence();
    const {viewPresetsEnabled} = useExperimentsState();

    // Complete list of resource types available in the account.
    // Only fetched when view presets are enabled and a saved diagram is loaded,
    // to avoid unnecessary API calls during diagram creation or navigation.
    const shouldFetchMetadata = viewPresetsEnabled && !!settings;
    const {data: resourcesMetadata} = useResourcesMetadata(null, {
        enabled: shouldFetchMetadata,
        refetchInterval: false,
        refetchOnWindowFocus: false,
    });
    const allResourceTypes = (resourcesMetadata?.resourceTypes ?? []).map(x => x.type);

    // Account resources loaded on first view preset change.
    // Kept disabled until explicitly triggered by fetchFullAccount().
    const accountId = shouldFetchMetadata ? diagramSettings?.accountId : undefined;
    const {
        data: fullAccountData,
        refetch: fetchFullAccount,
        isLoading: isLoadingFullAccount,
    } = useGetAccountResourceGraph(accountId, allResourceTypes);

    // Prevents repeated API calls during view preset switches
    const fullFetchTriggered = useRef(false);
    const [viewSwitchPending, setViewSwitchPending] = useState(null);

    // Load the previously selected view preset from localStorage,
    // discarding entries older than the TTL
    const [cachedViewPreset, setCachedViewPreset] = useState(() => {
        try {
            const stored = localStorage.getItem(getViewPresetStorageKey(name, visibility));
            if (!stored) return null;
            const parsed = JSON.parse(stored);
            if (parsed.timestamp && Date.now() - parsed.timestamp > PRESET_CACHE_TTL_MS) {
                localStorage.removeItem(getViewPresetStorageKey(name, visibility));
                return null;
            }
            return parsed;
        } catch (error) {
            console.warn('Failed to load cached view preset:', error);
            return null;
        }
    });

    // Sync the view preset to localStorage whenever settings update
    useEffect(() => {
        if (settings?.viewPreset) {
            try {
                const presetData = {
                    viewPreset: settings.viewPreset,
                    timestamp: Date.now()
                };
                localStorage.setItem(
                    getViewPresetStorageKey(name, visibility),
                    JSON.stringify(presetData)
                );
                setCachedViewPreset(presetData);
            } catch (error) {
                console.warn('Failed to cache view preset:', error);
            }
        }
    }, [settings?.viewPreset, name, visibility]);

    const [currentViewPreset, setCurrentViewPreset] = useState(null);
    const presetInitialised = useRef(false);

    // Stores all account resources (nodes and edges) after the initial fetch.
    // View switches after the first one filter from this cached data.
    const fullDiagramSnapshot = useRef(null);

    // Clear view preset state when the diagram changes
    useEffect(() => {
        presetInitialised.current = false;
        fullDiagramSnapshot.current = null;
        fullFetchTriggered.current = false;
        setCurrentViewPreset(null);
        setViewSwitchPending(null);
    }, [name, visibility]);

    // Establish the current view preset from available data sources
    useEffect(() => {
        if (presetInitialised.current) return;
        if (!settings && !diagramSettings) return;

        const savedPreset = cachedViewPreset?.viewPreset || settings?.viewPreset || diagramSettings?.viewPreset;
        if (savedPreset) {
            setCurrentViewPreset(savedPreset);
            presetInitialised.current = true;
        } else if (settings || diagramSettings) {
            setCurrentViewPreset(VIEW_PRESET_TYPES.FULL);
            presetInitialised.current = true;
        }
    }, [settings, diagramSettings?.viewPreset, cachedViewPreset?.viewPreset, name, visibility]);

    const updateCanvas = useCallback(
        newCanvas => {
            dispatchCanvas({
                type: 'setCanvas',
                canvas: newCanvas,
            });
        },
        [dispatchCanvas]
    );

    const updateResources = useCallback(() => {
        dispatchCanvas({
            type: 'setResources',
            resources: canvas.nodes(),
        });
    }, [canvas, dispatchCanvas]);

    useEffect(() => {
        if (canvas && !canvas.destroyed()) {
            const resetEvents = ['add', 'remove'];
            resetEvents.forEach(e =>
                canvas.on(e, () => setHasLoadedCosts(false))
            );
        }
    }, [canvas, setHasLoadedCosts]);

    useEffect(() => {
        if (canvas && !canvas.destroyed()) {
            setHasLoadedCosts(false);
        }
    }, [canvas, settings?.costsInterval, setHasLoadedCosts]);

    useEffect(() => {
        if (!hasLoadedCosts && canvas && !canvas.destroyed()) {
            hideCosts(canvas, updateCanvas, canvas.nodes().jsons());
        }
    }, [canvas, hasLoadedCosts, updateCanvas]);

    useEffect(() => {
        if (
            previousResources?.length &&
            !R.equals(resources, previousResources)
        ) {
            setHasUnsavedChanges(true);
        }
    }, [resources, previousResources]);

    // Rebuilds the canvas from the cached resources, showing only 
    // the resource types that belong to the selected view preset.
    const applyViewSwitch = useCallback((selectedPreset) => {
        if (!canvas || canvas.destroyed()) return;
        if (!fullDiagramSnapshot.current) return;

        const { nodes: allNodes, edges: allEdges } = fullDiagramSnapshot.current;

        const getNodeType = (node) => node.data?.type;
        const getNodeId = (node) => node.data?.id;
        const getParentId = (node) => node.data?.parent;
        const getResourceType = (node) => node.data?.properties?.resourceType;

        let filteredNodes;
        let filteredEdges;

        // Container nodes that form the compound hierarchy on the canvas.
        // Includes account, region, availabilityZone (camelCase as set by
        // processElements), vpc, subnet, and type (resource-type grouping boxes).
        const containerTypes = new Set([
            'account', 'region', 'availabilityZone', 'vpc', 'subnet', 'type'
        ]);

        if (selectedPreset === VIEW_PRESET_TYPES.FULL) {
            filteredNodes = [...allNodes];
            filteredEdges = [...allEdges];
        } else {
            const viewType = selectedPreset === VIEW_PRESET_TYPES.BUILDER ? 'builder' : 'security';
            const viewResourceTypes = new Set(getResourceTypesForViewPreset(viewType));

            // Collect leaf resource nodes that belong to the selected view
            const matchingResourceNodes = allNodes.filter(node => {
                if (containerTypes.has(getNodeType(node))) return false;
                const rt = getResourceType(node);
                return rt && viewResourceTypes.has(rt);
            });

            // Walk each matching node's parent chain to collect every
            // container needed to preserve the full compound hierarchy
            const neededParentIds = new Set();
            const nodeById = new Map(allNodes.map(n => [getNodeId(n), n]));

            for (const node of matchingResourceNodes) {
                let parentId = getParentId(node);
                while (parentId) {
                    if (neededParentIds.has(parentId)) break;
                    neededParentIds.add(parentId);
                    const parentNode = nodeById.get(parentId);
                    parentId = parentNode ? getParentId(parentNode) : undefined;
                }
            }

            const containerNodes = allNodes.filter(node => {
                if (!containerTypes.has(getNodeType(node))) return false;
                return neededParentIds.has(getNodeId(node));
            });

            filteredNodes = [...containerNodes, ...matchingResourceNodes];

            // Only keep edges where both endpoints are visible
            const visibleNodeIds = new Set(filteredNodes.map(n => getNodeId(n)));
            filteredEdges = allEdges.filter(edge =>
                visibleNodeIds.has(edge.data.source) && visibleNodeIds.has(edge.data.target)
            );
        }

        canvas.elements().remove();

        if (filteredNodes.length > 0) {
            // Strip stale position data from serialized nodes so the layout
            // engine recalculates compound hierarchy boundaries from scratch.
            // Without this, fcose uses old positions that were computed for
            // the full diagram, producing a flat scattered layout.
            const cleanNodes = filteredNodes.map(({ position, ...rest }) => rest);
            const cleanEdges = filteredEdges.map(({ position, ...rest }) => rest);

            addResources(
                canvas,
                updateCanvas,
                updateResources,
                { nodes: cleanNodes, edges: cleanEdges },
                getStandardLayout
            );
        } else {
            updateCanvas(canvas);
            updateResources();
        }

        setCurrentViewPreset(selectedPreset);

        // Persist the selected preset so it survives page refreshes
        try {
            const presetData = { viewPreset: selectedPreset, timestamp: Date.now() };
            localStorage.setItem(getViewPresetStorageKey(name, visibility), JSON.stringify(presetData));
            setCachedViewPreset(presetData);
        } catch (error) {
            console.warn('Failed to cache view preset:', error);
        }
    }, [canvas, updateCanvas, updateResources, name, visibility]);

    // Store the fetched account resources in the local cache and 
    // complete the pending view switch that triggered the fetch.
    useEffect(() => {
        if (!fullAccountData || !viewSwitchPending) return;

        const nodes = fullAccountData.filter(x => x.group === 'nodes');
        const nodeSet = new Set(nodes.map(n => n.data.id));
        const edges = fullAccountData.filter(x =>
            x.group === 'edges' &&
            nodeSet.has(x.data.source) &&
            nodeSet.has(x.data.target)
        );

        fullDiagramSnapshot.current = {
            nodes: [...nodes],
            edges: [...edges],
        };

        applyViewSwitch(viewSwitchPending);
        setViewSwitchPending(null);
    }, [fullAccountData, applyViewSwitch, viewSwitchPending]);

    // Processes view preset changes from the dropdown. Fetches all
    // account resources once on the first switch, then filters the
    // cached data locally for every switch after that.
    const handleViewSwitch = useCallback((selectedPreset) => {
        if (!canvas || canvas.destroyed()) return;

        // Resources already cached — apply the filter locally
        if (fullDiagramSnapshot.current) {
            applyViewSwitch(selectedPreset);
            return;
        }

        // No fetch needed for Full View — the diagram already contains
        // the complete resource set, so filter it directly.
        if (currentViewPreset === VIEW_PRESET_TYPES.FULL) {
            const elements = canvas.json().elements;
            if (elements && (elements.nodes?.length > 0 || elements.edges?.length > 0)) {
                fullDiagramSnapshot.current = {
                    nodes: elements.nodes ? [...elements.nodes] : [],
                    edges: elements.edges ? [...elements.edges] : [],
                };
                applyViewSwitch(selectedPreset);
                return;
            }
        }

        // Builder/Security View diagrams only have a subset of resources,
        // so we need a one-time fetch of all account resource types.
        if (accountId && allResourceTypes.length > 0 && !fullFetchTriggered.current) {
            fullFetchTriggered.current = true;
            setViewSwitchPending(selectedPreset);
            fetchFullAccount();
            return;
        }

        // Non-account diagrams: use the current diagram elements as the cache
        const elements = canvas.json().elements;
        if (!elements || (!elements.nodes?.length && !elements.edges?.length)) {
            console.warn('Cannot switch view: no diagram elements available');
            return;
        }
        fullDiagramSnapshot.current = {
            nodes: elements.nodes ? [...elements.nodes] : [],
            edges: elements.edges ? [...elements.edges] : [],
        };
        applyViewSwitch(selectedPreset);
    }, [canvas, currentViewPreset, accountId, allResourceTypes, applyViewSwitch, fetchFullAccount]);

    const onActionClick = id => {
        switch (id) {
            case 'fetch': {
                // Use getVisibleNodesFromSelection to respect filter visibility
                const visibleNodesForFetch = getVisibleNodesFromSelection(selectedResources);
                fetchResources(
                    canvas,
                    updateCanvas,
                    updateResources,
                    R.map(e => e.data('id'), visibleNodesForFetch),
                    graphResources
                );
                break;
            }
            case 'fit':
                fitToViewport(canvas, updateCanvas);
                break;
            case 'clear':
                clearGraph(canvas, updateCanvas, updateResources);
                break;
            case 'focus': {
                // Use getVisibleNodesFromSelection to respect filter visibility
                const visibleNodesForFocus = getVisibleNodesFromSelection(selectedResources);
                focusOnResources(
                    canvas,
                    updateCanvas,
                    updateResources,
                    R.map(e => e.data('id'), visibleNodesForFocus)
                );
                break;
            }
            case 'group':
                groupResources(canvas, updateCanvas);
                break;
            case 'export':
                setShowExport(true);
                break;
            case 'remove':
                removeResource(
                    canvas,
                    updateCanvas,
                    updateResources,
                    R.map(e => e.data('id'), selectedResources)
                );
                break;
            case 'save': {
                // Always save all elements regardless of filter state
                // Filtering is a view concern, not a data concern - filter state is persisted separately
                const elementsToSave = R.isEmpty(canvas.json().elements)
                    ? {nodes: [], edges: []}
                    : canvas.json().elements;

                putAsync({
                    key: canvas.data('name'),
                    level: canvas.data('visibility'),
                    type: 'application/json',
                    content: JSON.stringify({
                        ...elementsToSave,
                        settings: {
                            ...settings,
                            viewPreset: currentViewPreset,
                        },
                    }),
                })
                    .then(() =>
                        setStatusMessage({
                            type: 'success',
                            action: 'save',
                            message: `Last saved at ${dayjs(Date.now()).format('HH:mm')}`,
                        })
                    )
                    .then(() => setHasUnsavedChanges(false))
                    .catch(() =>
                        setStatusMessage({
                            type: 'error',
                            action: 'save',
                            message: 'Unable to save diagram',
                        })
                    );
                break;
            }
            case 'delete':
                setShowDeleteConfirm(true);
                break;
            default:
                break;
        }
    };

    const handleDelete = () => {
        setHasUnsavedChanges(false);
        canvas.destroy();
        removeAsync({key: name, level: visibility});
        // Remove the cached view preset for this diagram
        localStorage.removeItem(getViewPresetStorageKey(name, visibility));
        setShowDeleteConfirm(false);
        history.push(DRAW);
    };

    return (
        <>
            <Prompt
                when={hasUnsavedChanges}
                message={() =>
                    `Are you sure you want to leave this page without saving?`
                }
            />
            <ExportDiagramModal
                onDismiss={() => setShowExport(false)}
                visible={showExport}
                canvas={canvas}
                elements={getVisibleElements(canvas)}
                settings={settings}
            />
            <Modal
                onDismiss={() => setShowDeleteConfirm(false)}
                visible={showDeleteConfirm}
                closeAriaLabel="Close modal"
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button
                                onClick={() => setShowDeleteConfirm(false)}
                                variant="link"
                            >
                                Cancel
                            </Button>
                            <Button onClick={handleDelete} variant="primary">
                                Delete
                            </Button>
                        </SpaceBetween>
                    </Box>
                }
                header="Delete Diagram"
            >
                Permanently delete <strong>{name}</strong>? This cannot be
                undone.
            </Modal>
            <SpaceBetween size="s" direction="horizontal">
                {statusMessage != null && (
                    <Box margin={{vertical: 'xxs'}} display={'block'}>
                        <StatusIndicator type={statusMessage?.type}>
                            {' '}
                            {statusMessage?.message}
                        </StatusIndicator>
                    </Box>
                )}
                {/* View preset dropdown — only shown when the feature flag is enabled */}
                {viewPresetsEnabled && settings && currentViewPreset && (
                    <Box margin={{right: 's'}}>
                        <Select
                            selectedOption={{
                                label: getViewPresetLabel(currentViewPreset),
                                value: currentViewPreset
                            }}
                            onChange={({detail}) => {
                                const selectedPreset = detail.selectedOption.value;
                                handleViewSwitch(selectedPreset);
                            }}
                            options={[
                                {label: 'All AWS Resources', value: VIEW_PRESET_TYPES.FULL},
                                {label: 'Common AWS Builder Resources', value: VIEW_PRESET_TYPES.BUILDER},
                                {label: 'Common AWS Security Resources', value: VIEW_PRESET_TYPES.SECURITY},
                            ]}
                            disabled={isLoadingFullAccount}
                            loadingText="Loading all resources..."
                            statusType={isLoadingFullAccount ? 'loading' : 'finished'}
                            selectedAriaLabel="Selected"
                            placeholder="View preset"
                        />
                    </Box>
                )}

                {/* <Button
                    onClick={() => {
                        history.push(
                            COST_REPORT.replace(':name', name).replace(
                                ':visibility',
                                visibility
                            )
                        );
                    }}
                >
                    View Cost Report
                </Button>
                <Button onClick={handleRequestCosts} loading={loadingCosts}>
                    Load Costs
                </Button> */}
                <span title="Export your architecture diagram - Share with stakeholders, document your infrastructure, or integrate with other tools like Design Inspector">
                    <Button
                        onClick={() => onActionClick('export')}
                        disabled={R.isEmpty(
                            canvas?.json()?.elements?.nodes ?? []
                        )}
                    >
                        Export
                    </Button>
                </span>

                <ButtonDropdown
                    onItemClick={e => onActionClick(e.detail.id)}
                    expandableGroups
                    items={[
                        {
                            id: 'resources',
                            text: 'Resources',
                            items: [
                                {
                                    text: 'Expand',
                                    id: 'fetch',
                                },
                                {
                                    text: 'Focus',
                                    id: 'focus',
                                },
                                {
                                    text: 'Remove',
                                    id: 'remove',
                                },
                            ],
                            disabled:
                                !selectedResources || selectedResources.empty(),
                        },
                        {
                            id: 'diagram',
                            text: 'Diagram',
                            items: [
                                {text: 'Group', id: 'group'},
                                {text: 'Fit', id: 'fit'},
                                {text: 'Clear', id: 'clear'},
                            ],
                            disabled: R.isEmpty(
                                canvas?.json()?.elements?.nodes ?? []
                            ),
                        },
                        {
                            id: 'save',
                            text: 'Save',
                        },
                        {
                            id: 'delete',
                            text: 'Delete',
                        },
                    ]}
                    variant="primary"
                >
                    Actions
                </ButtonDropdown>
            </SpaceBetween>
        </>
    );
};

DiagramControlPanel.propTypes = {
    settings: PropTypes.shape({
        costsInterval: PropTypes.shape({
            type: PropTypes.string,
            startDate: PropTypes.string,
            endDate: PropTypes.string,
            unit: PropTypes.string,
            amount: PropTypes.number,
        }),
        hideSelected: PropTypes.bool,
        hideEdges: PropTypes.bool,
        accounts: PropTypes.array,
        regions: PropTypes.array,
        resourceTypes: PropTypes.array,
        viewPreset: PropTypes.string,
    }),
};

export default DiagramControlPanel;
